import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Loader2, GraduationCap, X, Sparkles, FileUp, Trash2, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { updateEmployeeSchedule, updateEmployee, getEmployeeSchedule, parseSchoolCalendar, applyCalendarOverrides, setDayException } from "../../services/api";

export const OVERRIDE_LABELS = { school: "École", work: "Travail", rest: "Repos", holiday: "Férié" };
const OVERRIDE_COLORS = { school: "bg-amber-100 text-amber-800", work: "bg-teal-100 text-teal-800", rest: "bg-slate-200 text-slate-600", holiday: "bg-red-100 text-red-700" };

const DAYS = [
  { key: "monday", label: "Lundi" },
  { key: "tuesday", label: "Mardi" },
  { key: "wednesday", label: "Mercredi" },
  { key: "thursday", label: "Jeudi" },
  { key: "friday", label: "Vendredi" },
  { key: "saturday", label: "Samedi" },
  { key: "sunday", label: "Dimanche" },
];

export const COLOR_PALETTE = ["#e91e63", "#3f51b5", "#009688", "#ff9800", "#9c27b0", "#2196f3",
  "#4caf50", "#f44336", "#795548", "#607d8b", "#00bcd4", "#8bc34a"];

const EMPTY_DAY = { mode: "off", morning_start: "", morning_end: "", afternoon_start: "", afternoon_end: "" };

const slotHours = (s, e) => {
  if (!s || !e) return 0;
  const [sh, sm] = s.split(":").map(Number);
  let [eh, em] = e.split(":").map(Number);
  if (eh === 0 && em === 0) eh = 24;
  const diff = (eh * 60 + em - sh * 60 - sm) / 60;
  return diff > 0 ? diff : 0;
};

const dayHours = (d) => (d.mode !== "work" ? 0 : slotHours(d.morning_start, d.morning_end) + slotHours(d.afternoon_start, d.afternoon_end));

const MODES = [
  { value: "off", label: "Repos" },
  { value: "work", label: "Travail" },
  { value: "school", label: "École" },
];

export const ScheduleEditorDialog = ({ open, onClose, employee, schedule, schoolWeeks: initialWeeks, onSaved }) => {
  const [days, setDays] = useState({});
  const [color, setColor] = useState("#26788A");
  const [schoolWeeks, setSchoolWeeks] = useState([]);
  const [weekInput, setWeekInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [calInfo, setCalInfo] = useState({ total: 0, counts: {}, source: null, manual: {} });
  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [excDate, setExcDate] = useState("");
  const [excType, setExcType] = useState("work");
  const fileRef = useRef(null);

  const loadCalInfo = () => {
    if (!employee?.id) return;
    getEmployeeSchedule(employee.id)
      .then((r) => setCalInfo({
        total: r.data.calendar_total || 0,
        counts: r.data.calendar_counts || {},
        source: r.data.school_calendar_source,
        manual: r.data.manual_overrides || {},
      }))
      .catch(() => {});
  };

  useEffect(() => {
    if (open) {
      const init = {};
      DAYS.forEach(({ key }) => {
        const d = schedule?.[key] || {};
        init[key] = {
          mode: d.is_school ? "school" : d.is_off === false ? "work" : "off",
          morning_start: d.morning_start || "",
          morning_end: d.morning_end || "",
          afternoon_start: d.afternoon_start || "",
          afternoon_end: d.afternoon_end || "",
        };
      });
      setDays(init);
      setColor(employee?.color || "#26788A");
      setSchoolWeeks(initialWeeks || []);
      setWeekInput("");
      setParseResult(null);
      setParsing(false);
      loadCalInfo();
    }
  }, [open, schedule, employee, initialWeeks]); // eslint-disable-line react-hooks/exhaustive-deps

  const setDay = (key, patch) => setDays((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  const copyPrevious = (idx) => {
    if (idx === 0) return;
    setDay(DAYS[idx].key, { ...days[DAYS[idx - 1].key] });
  };

  const addSchoolWeek = () => {
    if (!weekInput || schoolWeeks.includes(weekInput)) return;
    setSchoolWeeks([...schoolWeeks, weekInput].sort());
    setWeekInput("");
  };

  const handleCalendarFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setParsing(true);
    setParseResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await parseSchoolCalendar(employee.id, fd);
      setParseResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'analyse du calendrier");
    } finally {
      setParsing(false);
    }
  };

  const applyCalendar = async () => {
    try {
      setApplying(true);
      await applyCalendarOverrides(employee.id, {
        days: parseResult.days,
        mode: "replace",
        source_name: parseResult.school_name || parseResult.source_name,
      });
      toast.success(`Calendrier appliqué : ${parseResult.counts?.school || 0} jours école, ${parseResult.counts?.work || 0} jours entreprise`);
      setParseResult(null);
      loadCalInfo();
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setApplying(false);
    }
  };

  const clearCalendar = async () => {
    if (!window.confirm("Retirer tout le calendrier école importé pour cet employé ?")) return;
    try {
      await applyCalendarOverrides(employee.id, { days: {}, mode: "clear" });
      toast.success("Calendrier école retiré");
      loadCalInfo();
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const addException = async () => {
    if (!excDate) return;
    try {
      const res = await setDayException(employee.id, { date: excDate, type: excType });
      setCalInfo((c) => ({ ...c, manual: res.data.manual_overrides }));
      setExcDate("");
      toast.success("Exception ajoutée");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const removeException = async (d) => {
    try {
      const res = await setDayException(employee.id, { date: d, type: null });
      setCalInfo((c) => ({ ...c, manual: res.data.manual_overrides }));
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const monthLabel = (ym) => new Date(ym + "-01T00:00:00").toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });

  const totalHours = DAYS.reduce((acc, { key }) => acc + dayHours(days[key] || EMPTY_DAY), 0);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {};
      DAYS.forEach(({ key }) => {
        const d = days[key];
        payload[key] = {
          is_off: d.mode === "off",
          is_school: d.mode === "school",
          morning_start: d.mode === "work" ? d.morning_start || null : null,
          morning_end: d.mode === "work" ? d.morning_end || null : null,
          afternoon_start: d.mode === "work" ? d.afternoon_start || null : null,
          afternoon_end: d.mode === "work" ? d.afternoon_end || null : null,
        };
      });
      await updateEmployeeSchedule(employee.id, payload, schoolWeeks);
      if (color !== employee.color) {
        await updateEmployee(employee.id, { color });
      }
      toast.success("Planning enregistré");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (!employee) return null;

  const weekLabel = (w) => {
    const [y, wk] = w.split("-W");
    return `S${wk} ${y}`;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="responsive-dialog max-w-3xl max-h-[90dvh] overflow-y-auto" data-testid="schedule-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="w-3.5 h-3.5 rounded-full inline-block" style={{ backgroundColor: color }}></span>
            Planning — {employee.first_name} {employee.last_name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Couleur de l'employé</p>
            <div className="flex flex-wrap gap-2">
              {COLOR_PALETTE.map((c) => (
                <button
                  key={c}
                  className={`w-7 h-7 rounded-full transition-transform hover:scale-110 ${color === c ? "ring-2 ring-offset-2 ring-slate-800 scale-110" : ""}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                  data-testid={`color-swatch-${c.slice(1)}`}
                />
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Semaine type</p>
            {DAYS.map(({ key, label }, idx) => {
              const d = days[key] || EMPTY_DAY;
              return (
                <div key={key} className={`grid grid-cols-1 sm:grid-cols-[100px_180px_1fr_1fr_auto] gap-2 items-center p-2 rounded-lg ${d.mode === "off" ? "bg-slate-50" : d.mode === "school" ? "bg-amber-50" : "bg-teal-50/50"}`}>
                  <span className={`text-sm font-medium ${key === "sunday" ? "text-red-600" : "text-slate-700"}`}>{label}</span>
                  <div className="flex gap-1">
                    {MODES.map((m) => (
                      <button
                        key={m.value}
                        className={`text-[11px] px-2.5 py-1 rounded-full font-medium transition-colors ${
                          d.mode === m.value
                            ? m.value === "school" ? "bg-amber-500 text-white" : m.value === "work" ? "bg-[#26788A] text-white" : "bg-slate-500 text-white"
                            : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-100"
                        }`}
                        onClick={() => setDay(key, { mode: m.value })}
                        data-testid={`sched-${key}-mode-${m.value}`}
                      >
                        {m.value === "school" && <GraduationCap size={10} className="inline mr-0.5 -mt-0.5" />}
                        {m.label}
                      </button>
                    ))}
                  </div>
                  {d.mode === "work" ? (
                    <>
                      <div className="flex items-center gap-1">
                        <Input type="time" className="h-8 text-xs" value={d.morning_start} onChange={(e) => setDay(key, { morning_start: e.target.value })} data-testid={`sched-${key}-ms`} />
                        <span className="text-slate-400">–</span>
                        <Input type="time" className="h-8 text-xs" value={d.morning_end} onChange={(e) => setDay(key, { morning_end: e.target.value })} data-testid={`sched-${key}-me`} />
                      </div>
                      <div className="flex items-center gap-1">
                        <Input type="time" className="h-8 text-xs" value={d.afternoon_start} onChange={(e) => setDay(key, { afternoon_start: e.target.value })} data-testid={`sched-${key}-as`} />
                        <span className="text-slate-400">–</span>
                        <Input type="time" className="h-8 text-xs" value={d.afternoon_end} onChange={(e) => setDay(key, { afternoon_end: e.target.value })} data-testid={`sched-${key}-ae`} />
                      </div>
                    </>
                  ) : (
                    <div className="sm:col-span-2 text-xs text-slate-400 italic">
                      {d.mode === "school" ? "Journée à l'école (alternance)" : "Jour de repos"}
                    </div>
                  )}
                  <div className="flex items-center gap-2 justify-end">
                    <span className="text-xs font-mono text-slate-500 w-12 text-right">{dayHours(d).toFixed(1)}h</span>
                    {idx > 0 && (
                      <button className="text-[10px] text-teal-600 hover:underline" onClick={() => copyPrevious(idx)}>↑ copier</button>
                    )}
                  </div>
                </div>
              );
            })}
            <p className="text-right text-sm font-semibold text-slate-700" data-testid="schedule-total-hours">
              Total hebdomadaire : <span className="font-mono text-[#26788A]">{totalHours.toFixed(1)} h</span>
            </p>
          </div>

          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1.5">
              <GraduationCap size={13} className="text-amber-500" /> Alternance — semaines complètes à l'école
            </p>
            <p className="text-[11px] text-slate-400 mb-2">Ces semaines remplacent entièrement la semaine type (l'employé est à l'école toute la semaine).</p>
            <div className="flex items-center gap-2 mb-2">
              <Input type="week" className="w-48 h-8 text-xs" value={weekInput} onChange={(e) => setWeekInput(e.target.value)} data-testid="school-week-input" />
              <Button size="sm" variant="outline" onClick={addSchoolWeek} disabled={!weekInput} data-testid="add-school-week-btn">Ajouter</Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {schoolWeeks.length === 0 && <span className="text-xs text-slate-300">Aucune semaine école</span>}
              {schoolWeeks.map((w) => (
                <span key={w} className="flex items-center gap-1 text-xs bg-amber-100 text-amber-800 rounded-full px-2.5 py-1" data-testid={`school-week-chip-${w}`}>
                  <GraduationCap size={11} /> {weekLabel(w)}
                  <button className="hover:text-red-600" onClick={() => setSchoolWeeks(schoolWeeks.filter((x) => x !== w))}>
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
          </div>
          <div className="border-t border-slate-100 pt-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1.5">
              <Sparkles size={13} className="text-[#26788A]" /> Calendrier école (import PDF analysé par IA)
            </p>
            <p className="text-[11px] text-slate-400 mb-2">Importez le calendrier d'alternance de l'école (PDF ou image) : l'IA détecte les jours d'école, d'entreprise et de repos, et les applique jour par jour au planning.</p>
            {calInfo.total > 0 && !parseResult && (
              <div className="flex items-center gap-2 mb-2 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2" data-testid="school-cal-current">
                <Check size={14} className="text-emerald-600 shrink-0" />
                <span className="text-xs text-emerald-700 flex-1">
                  Calendrier importé{calInfo.source ? ` — ${calInfo.source}` : ""} : {["school", "work", "holiday", "rest"].filter((t) => calInfo.counts[t]).map((t) => `${calInfo.counts[t]} ${OVERRIDE_LABELS[t].toLowerCase()}`).join(" · ")}
                </span>
                <button className="text-slate-400 hover:text-red-500" onClick={clearCalendar} title="Retirer" data-testid="clear-school-cal-btn">
                  <Trash2 size={14} />
                </button>
              </div>
            )}
            <input ref={fileRef} type="file" accept=".pdf,image/png,image/jpeg,image/webp" className="hidden" onChange={handleCalendarFile} data-testid="school-cal-file-input" />
            <Button size="sm" variant="outline" className="text-[#26788A] border-[#26788A]/30" onClick={() => fileRef.current?.click()} disabled={parsing} data-testid="upload-school-cal-btn">
              {parsing ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <FileUp size={13} className="mr-1.5" />}
              {parsing ? "Analyse IA en cours…" : "Importer un calendrier"}
            </Button>
            {parseResult && (
              <div className="mt-2 bg-teal-50/60 border border-teal-100 rounded-lg p-3" data-testid="school-cal-result">
                <p className="text-xs font-semibold text-slate-700 mb-1">
                  {parseResult.school_name ? `${parseResult.school_name} — ` : ""}
                  <span className="font-normal text-slate-400">({parseResult.period_start} → {parseResult.period_end})</span>
                </p>
                <div className="flex flex-wrap gap-1 mb-1.5">
                  {["school", "work", "holiday", "rest"].filter((t) => parseResult.counts?.[t]).map((t) => (
                    <span key={t} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${OVERRIDE_COLORS[t]}`}>{OVERRIDE_LABELS[t]} : {parseResult.counts[t]}j</span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1 mb-2">
                  {Object.entries(parseResult.per_month || {}).map(([m, n]) => (
                    <span key={m} className="text-[10px] font-medium bg-white border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full">{monthLabel(m)} : {n}j école</span>
                  ))}
                </div>
                {parseResult.notes && <p className="text-[10px] text-slate-400 italic mb-2">{parseResult.notes}</p>}
                <div className="flex gap-2">
                  <Button size="sm" className="h-7 bg-[#26788A] hover:bg-[#1D5E6C] text-white text-xs" onClick={applyCalendar} disabled={applying} data-testid="apply-school-cal-btn">
                    {applying && <Loader2 size={12} className="animate-spin mr-1" />} Appliquer au planning
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400" onClick={() => setParseResult(null)}>Ignorer</Button>
                </div>
              </div>
            )}

            <div className="mt-3 pt-3 border-t border-slate-100" data-testid="day-exceptions-section">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Exceptions journalières</p>
              <p className="text-[11px] text-slate-400 mb-2">Modifiez un jour précis (prioritaire sur le calendrier importé et la semaine type).</p>
              <div className="flex items-center gap-2 mb-2">
                <Input type="date" className="w-40 h-8 text-xs" value={excDate} onChange={(e) => setExcDate(e.target.value)} data-testid="exception-date-input" />
                <div className="flex gap-1">
                  {Object.entries(OVERRIDE_LABELS).map(([t, l]) => (
                    <button key={t} className={`text-[11px] px-2.5 py-1 rounded-full font-medium transition-colors ${excType === t ? OVERRIDE_COLORS[t] + " ring-1 ring-slate-300" : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-100"}`}
                      onClick={() => setExcType(t)} data-testid={`exception-type-${t}`}>{l}</button>
                  ))}
                </div>
                <Button size="sm" variant="outline" className="h-8" onClick={addException} disabled={!excDate} data-testid="add-exception-btn">Ajouter</Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Object.keys(calInfo.manual).length === 0 && <span className="text-xs text-slate-300">Aucune exception</span>}
                {Object.entries(calInfo.manual).sort().map(([d, t]) => (
                  <span key={d} className={`flex items-center gap-1 text-[11px] font-medium rounded-full px-2.5 py-1 ${OVERRIDE_COLORS[t] || "bg-slate-100"}`} data-testid={`exception-chip-${d}`}>
                    {new Date(d + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" })} · {OVERRIDE_LABELS[t] || t}
                    <button className="hover:text-red-600" onClick={() => removeException(d)} data-testid={`remove-exception-${d}`}>
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button className="bg-[#26788A] hover:bg-[#1D5E6C] text-white" onClick={handleSave} disabled={saving} data-testid="save-schedule-btn">
            {saving && <Loader2 size={14} className="animate-spin mr-1" />}
            Enregistrer le planning
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
